import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { ILocalizedInformation, IProduct, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import RepairabilityIndexModalEnum from '../../enums/RepairabilityIndexModalEnum.js';
import type IRepairabilityIndexTranslations from '../../types/IRepairabilityIndexTranslations.js';
import RepairabilityIndexUtility from '../../utilities/RepairabilityIndexUtility.js';
import RepairabilityIndexCard from '../repairability-index-card/RepairabilityIndexCard.js';
import RepairabilityIndexStyle from './RepairabilityIndexStyle.js';

/**
 * The Repairability Index component is used to communicate a product's repairability index. The size of the repairability index adapts to the font size and it should always be equivalent to the font size of the price.
 *
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */

export default class RepairabilityIndex extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IRepairabilityIndexTranslations | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Visible modal.
	@property({ type: String })
	public visibleModal: RepairabilityIndexModalEnum | null = null;

	private sheetElement: SkapaSheet | null = null;

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${RepairabilityIndexStyle.getStyle()}
			</style>

			${this.disableLinks ? this.getImageAsButton() : this.getImageAsLink()}
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-focus-outline', FocusOutline);
		this.customElements.define('kompis-repairability-index-card', RepairabilityIndexCard);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === RepairabilityIndexModalEnum.repairabilityIndexCard) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}
		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IRepairabilityIndexTranslations {
		return this.translations
			? this.translations
			: <IRepairabilityIndexTranslations>this.localizedInformation?.translations;
	}

	private getImageAsButton(): RenderResult {
		const imageElement = this.getImageElement();
		if (!imageElement) {
			return this.getHiddenElement();
		}

		return html`
			<kompis-focus-outline .theme="${this.theme}">
				<div
					class="imageContainer"
					tabindex="0"
					role="button"
					@keydown="${(event: KeyboardEvent) => {
						if (event.key === 'Enter' || event.key === ' ') {
							this.sendModalOpen();
						}
					}}"
					@click="${(event: Event) => {
						event.preventDefault();
						event.stopPropagation();
						this.sendModalOpen();
					}}"
				>
					${imageElement}
				</div>
			</kompis-focus-outline>
		`;
	}

	private getImageAsLink(): RenderResult {
		const imageElement = this.getImageElement();
		if (!imageElement) {
			return this.getHiddenElement();
		}

		const documentUrl =
			RepairabilityIndexUtility.getDocumentUrl(this.product, 'pdf') ??
			RepairabilityIndexUtility.getDocumentUrl(this.product, 'svg');

		if (!documentUrl) {
			return this.getHiddenElement();
		}

		return html`
			<kompis-focus-outline .theme="${this.theme}">
				<a href="${documentUrl}" target="_blank" class="imageContainer">${imageElement}</a>
			</kompis-focus-outline>
		`;
	}

	/**
	 * Used when nothing should be rendered.
	 * The benefit compared to returning null, is that any padding/margin applied to this component by parents components is disregarded.
	 *
	 * @returns Template result for a hidden element.
	 */
	private getHiddenElement(): RenderResult {
		return html`
			<style>
				:host {
					display: none;
				}
			</style>
		`;
	}

	private getImageElement(): RenderResult {
		const imageUrl =
			RepairabilityIndexUtility.getImageUrl(this.product, 'svg') ??
			RepairabilityIndexUtility.getImageUrl(this.product, 'png');

		if (!imageUrl) {
			return null;
		}

		return html`<img
			alt="${RepairabilityIndexUtility.getImageAltText(this.product)}"
			src="${imageUrl}"
		/>`;
	}

	private sendModalOpen(): void {
		this.dispatchEvent(
			new CustomEvent('modalOpen', {
				bubbles: true,
				detail: {
					visibleModal: RepairabilityIndexModalEnum.repairabilityIndexCard
				}
			})
		);
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
		}
	}

	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		const sheetElement: SkapaSheet = this.createSheetElement();
		this.sheetElement = sheetElement;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const cardElement = this.createCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(cardElement);
		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		sheetElement.size = 'large';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';
		sheetElement.addEventListener('closerequest', () => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null
						}
					})
				);
			}
		});
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		sheetHeaderElement.appendChild(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(): RepairabilityIndexCard {
		const cardElement = this.createInternalElement('kompis-repairability-index-card');
		cardElement.product = this.product;

		return cardElement;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-repairability-index': RepairabilityIndex;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}
